/* ======================================================================
   Bestseller Shirts Carousel  —  BSCarousel
   Self-contained IIFE. Pointer Events unified for mouse, pen and
   touchscreen-laptop. Touch-only devices are handled natively by CSS
   scroll-snap + overflow-x so we never touch the mobile path.
   ====================================================================== */
(() => {
  'use strict';

  /* Thresholds ───────────────────────────────────────────────────────── */
  const LOCK_PX  = 6;    // px — axis lock: wait before committing to H or V
  const SNAP_VEL = 0.3;  // px/ms — above this velocity we flick one product
  const SETTLE   = 80;   // ms — wait after native scroll before syncing counter

  class BSCarousel {
    constructor(el) {
      this.el      = el;
      this.track   = el.querySelector('.bsc-track');
      this.slides  = Array.from(el.querySelectorAll('.bsc-slide'));
      this.btnPrev = el.querySelector('.bsc-btn-prev');
      this.btnNext = el.querySelector('.bsc-btn-next');
      this.elCurr  = el.querySelector('.bsc-curr');
      this.elTotal = el.querySelector('.bsc-total');

      if (!this.track || this.slides.length === 0) return;

      this.total        = this.slides.length;
      this.idx          = 0;
      this._ptr         = null;  // active pointer drag state (null when idle)
      this._settleTimer = null;

      /* Pre-bound references — required so removeEventListener can match */
      this._b = {
        scroll:    ()  => this._onScroll(),
        ptrdown:   (e) => this._ptrDown(e),
        ptrmove:   (e) => this._ptrMove(e),
        ptrup:     (e) => this._ptrUp(e),
        ptrcancel: (e) => this._ptrCancel(e),
        keydown:   (e) => this._onKey(e),
      };

      this._live = false;
      this._boot();
    }

    /* ── Wire permanent listeners ──────────────────────────────────── */
    _boot() {
      const t = this.track;
      t.setAttribute('tabindex', '0');
      t.addEventListener('scroll',      this._b.scroll,   { passive: true });
      t.addEventListener('pointerdown', this._b.ptrdown);
      t.addEventListener('keydown',     this._b.keydown);
      this.btnPrev?.addEventListener('click', () => this._step(-1));
      this.btnNext?.addEventListener('click', () => this._step(1));
      this._live = true;
      this._syncUI();
    }

    /* ── Native-scroll settle (touch / trackpad horizontal swipe) ──── */
    _onScroll() {
      clearTimeout(this._settleTimer);
      this._settleTimer = setTimeout(() => {
        const n = this._nearestIdx();
        if (n !== this.idx) { this.idx = n; this._syncUI(); }
      }, SETTLE);
    }

    /* ── Pointer down ───────────────────────────────────────────────── */
    _ptrDown(e) {
      /* Primary button only; ignore if another pointer is already active */
      if (e.button !== 0 || this._ptr) return;

      /*
       * Touch-type pointers: the browser (+ CSS scroll-snap + overflow-x)
       * already handles touch natively and it works well. We skip it here
       * so mobile and touchscreen-laptop touch paths are completely untouched.
       */
      if (e.pointerType === 'touch') return;

      this._ptr = {
        id:          e.pointerId,
        startX:      e.clientX,
        startY:      e.clientY,
        lastX:       e.clientX,
        lastT:       performance.now(),
        scrollStart: this.track.scrollLeft,
        velX:        0,
        committed:   false,   // true once axis is locked to horizontal
        canceled:    false,   // true if we decided it was vertical
        dragged:     false,   // true once committed + moved at least 1px
      };

      /* Capture the pointer so we keep receiving events if the pointer
         leaves the track element (e.g. moves over a product image). */
      this.track.setPointerCapture(e.pointerId);

      /* Add transient listeners; removed in every exit path */
      this.track.addEventListener('pointermove',   this._b.ptrmove);
      this.track.addEventListener('pointerup',     this._b.ptrup);
      this.track.addEventListener('pointercancel', this._b.ptrcancel);
    }

    /* ── Pointer move ───────────────────────────────────────────────── */
    _ptrMove(e) {
      if (!this._ptr || this._ptr.id !== e.pointerId || this._ptr.canceled) return;

      const dx = e.clientX - this._ptr.startX;
      const dy = e.clientY - this._ptr.startY;

      /* ── Axis-lock decision (runs once, after LOCK_PX threshold) ── */
      if (!this._ptr.committed) {
        if (Math.abs(dx) < LOCK_PX && Math.abs(dy) < LOCK_PX) return;

        if (Math.abs(dy) >= Math.abs(dx)) {
          /*
           * Primarily vertical → release capture so the browser can
           * handle the normal page scroll. We do nothing further.
           */
          this._ptr.canceled = true;
          this.track.releasePointerCapture(this._ptr.id);
          this._dropTransientListeners();
          this._ptr = null;
          return;
        }

        /* Commit to horizontal drag */
        this._ptr.committed = true;
        this._ptr.dragged   = true;

        /*
         * Disable scroll-snap for the duration of the drag so the
         * browser cannot snap between frames and cause jerkiness.
         */
        this.track.classList.add('bsc-dragging');
      }

      /* Track instantaneous velocity (px / ms) for flick detection */
      const now = performance.now();
      const dt  = now - this._ptr.lastT;
      if (dt > 0) {
        this._ptr.velX = (e.clientX - this._ptr.lastX) / dt;
      }
      this._ptr.lastX = e.clientX;
      this._ptr.lastT = now;

      /*
       * Direct scroll: dragging the pointer LEFT increases scrollLeft
       * (we see products further to the right). This is 1:1 with the
       * pointer so it feels immediate and cursor-accurate.
       */
      this.track.scrollLeft = this._ptr.scrollStart - dx;
    }

    /* ── Pointer up ─────────────────────────────────────────────────── */
    _ptrUp(e) {
      if (!this._ptr || this._ptr.id !== e.pointerId) return;

      const { committed, dragged, velX } = this._ptr;
      this._dropTransientListeners();
      this._ptr = null;

      /* Pure click — nothing happened, restore state cleanly */
      if (!committed || !dragged) {
        this.track.classList.remove('bsc-dragging');
        return;
      }

      /*
       * Block only the immediate synthetic `click` that the browser fires
       * right after pointerup (< 50 ms). A deliberate tap that arrives
       * later is never intercepted, so product links work after swiping.
       */
      const _t = this.track;
      const _b = (ev) => { ev.preventDefault(); ev.stopPropagation(); _t.removeEventListener('click', _b, true); };
      _t.addEventListener('click', _b, true);
      setTimeout(() => _t.removeEventListener('click', _b, true), 50);

      /*
       * Target index:
       *   • Fast flick (|velX| ≥ SNAP_VEL): step one product in the
       *     direction of the flick, measured from the current nearest index
       *     (not from the drag-start index — handles long drags correctly).
       *   • Slow or short drag: snap to nearest product based on
       *     the current scrollLeft position.
       */
      const nearestNow = this._nearestIdx();
      const targetIdx = (Math.abs(velX) >= SNAP_VEL)
        ? Math.max(0, Math.min(nearestNow + (velX > 0 ? -1 : 1), this.total - 1))
        : nearestNow;

      const targetScroll = targetIdx * (this._slideW() + this._gap());

      /*
       * Snap sequence:
       *   1. While scroll-snap is still off (bsc-dragging active), jump
       *      directly to the exact snap-point scroll position.
       *   2. Re-enable scroll-snap in the next frame. Since we are
       *      already sitting exactly on a snap point, the browser will
       *      not move us — there is no jump.
       *
       * This gives an instant, stable snap that feels responsive.
       */
      this.track.scrollLeft = targetScroll;
      this.idx = targetIdx;
      this._syncUI();

      requestAnimationFrame(() => {
        this.track.classList.remove('bsc-dragging');
      });
    }

    /* ── Pointer cancel (system interruption: alt-tab, phone call…) ── */
    _ptrCancel(e) {
      if (!this._ptr || this._ptr.id !== e.pointerId) return;
      this._dropTransientListeners();
      this._ptr = null;
      this.track.classList.remove('bsc-dragging');
      /* Snap to nearest so the carousel is not left mid-product */
      this._goTo(this._nearestIdx());
    }

    /* ── Remove transient move / up / cancel listeners ─────────────── */
    _dropTransientListeners() {
      this.track.removeEventListener('pointermove',   this._b.ptrmove);
      this.track.removeEventListener('pointerup',     this._b.ptrup);
      this.track.removeEventListener('pointercancel', this._b.ptrcancel);
    }

    /* ── Keyboard navigation ────────────────────────────────────────── */
    _onKey(e) {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); this._step(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this._step(1);  }
    }

    /* ── Step by ±1 ─────────────────────────────────────────────────── */
    _step(d) { this._goTo(this.idx + d); }

    /* ── Scroll to absolute index ───────────────────────────────────── */
    _goTo(i, instant = false) {
      i = Math.max(0, Math.min(i, this.total - 1));
      this.idx = i;
      const target = i * (this._slideW() + this._gap());
      this.track.scrollTo({ left: target, behavior: instant ? 'instant' : 'smooth' });
      this._syncUI();
    }

    /* ── Update counter + button disabled states ─────────────────────  */
    _syncUI() {
      const i = this.idx, n = this.total;
      if (this.elCurr)  this.elCurr.textContent  = i + 1;
      if (this.elTotal) this.elTotal.textContent  = n;
      if (this.btnPrev) this.btnPrev.disabled = i === 0;
      if (this.btnNext) this.btnNext.disabled = i >= n - 1;
    }

    /* ── Nearest snapped slide based on current scrollLeft ──────────── */
    _nearestIdx() {
      const sl = this.track.scrollLeft;
      const w  = this._slideW();
      const g  = this._gap();
      return (w + g > 0)
        ? Math.max(0, Math.min(Math.round(sl / (w + g)), this.total - 1))
        : 0;
    }

    _slideW() { return this.slides[0]?.offsetWidth ?? 0; }
    _gap()    { return parseFloat(getComputedStyle(this.track).columnGap) || 0; }

    /* ── Re-initialise after Theme Editor section:load ──────────────── */
    _reset() {
      /* Cancel any active drag first */
      if (this._ptr) {
        this._dropTransientListeners();
        this.track.classList.remove('bsc-dragging');
        this._ptr = null;
      }
      this.slides = Array.from(this.el.querySelectorAll('.bsc-slide'));
      this.total  = this.slides.length;
      this.idx    = 0;
      this.track.scrollLeft = 0;
      this._syncUI();
    }

    /* ── Full teardown on section:unload ────────────────────────────── */
    destroy() {
      clearTimeout(this._settleTimer);
      this._dropTransientListeners();
      if (this._live) {
        this.track.removeEventListener('scroll',      this._b.scroll);
        this.track.removeEventListener('pointerdown', this._b.ptrdown);
        this.track.removeEventListener('keydown',     this._b.keydown);
        this._live = false;
      }
      this._ptr = null;
    }
  }

  /* ── Initialise all carousels present in the DOM ───────────────────── */
  function boot() {
    document.querySelectorAll('[data-bsc-root]').forEach((el) => {
      if (!el._bsc) el._bsc = new BSCarousel(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme Editor events ─────────────────────────────────────────────── */
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target?.querySelector('[data-bsc-root]');
    if (!root) return;
    root._bsc?.destroy();
    root._bsc = new BSCarousel(root);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target?.querySelector('[data-bsc-root]');
    if (!root?._bsc) return;
    root._bsc.destroy();
    root._bsc = null;
  });
})();
